// ============================================================
// Candidate Profile — Human-Readable Data + AI Score Details
// ============================================================

import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useState } from 'react';
import {
    ArrowLeft, Mail, Phone, MapPin, Calendar, FileText,
    BarChart3, BookOpen, Edit2, Save, X, Check,
    AlertTriangle, Star, ChevronDown, MessageSquare, Award, Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import { generateCandidateResultPDF } from '../utils/pdfGenerator';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function CandidateProfilePage() {
    const { id } = useParams();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('overview');

    const { data, isLoading } = useQuery({
        queryKey: ['candidate', id],
        queryFn: () => api.get(`/candidates/${id}`),
    });

    const candidate = data?.data;

    if (isLoading) return <div className="loading-page"><div className="loading-spinner" /></div>;
    if (!candidate) return <div className="card"><div className="empty-state"><h3>Candidate not found</h3></div></div>;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <BookOpen size={16} /> },
        { id: 'application', label: 'Application', icon: <FileText size={16} /> },
        { id: 'assessments', label: 'Assessments', icon: <BarChart3 size={16} /> },
        { id: 'notes', label: 'Notes', icon: <MessageSquare size={16} /> },
    ];

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <Link to="/jobs" style={{ color: '#6b7280', fontSize: '14px', textDecoration: 'none' }}>
                        <ArrowLeft size={14} style={{ display: 'inline', marginRight: 4 }} /> Back
                    </Link>
                    <h1 className="mt-2">{candidate.name || 'Unknown Candidate'}</h1>
                    <p className="subtitle">
                        {candidate.email && <span className="mr-4"><Mail size={14} style={{ display: 'inline', marginRight: 4 }} />{candidate.email}</span>}
                        {candidate.phone && <span className="mr-4"><Phone size={14} style={{ display: 'inline', marginRight: 4 }} />{candidate.phone}</span>}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {candidate.stage && (
                        <span className="badge badge-accent" style={{ fontSize: '14px', padding: '8px 16px' }}>
                            {candidate.stage.name || candidate.stage}
                        </span>
                    )}
                    <button
                        className="btn btn-primary btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={() => {
                            try {
                                generateCandidateResultPDF(candidate);
                                toast.success('PDF downloaded');
                            } catch (err) {
                                toast.error('Failed to generate PDF');
                            }
                        }}
                    >
                        <Download size={14} /> Download PDF
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                {tabs.map(tab => (
                    <button key={tab.id} className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}>
                        {tab.icon}
                        <span style={{ marginLeft: 4 }}>{tab.label}</span>
                    </button>
                ))}
            </div>

            {activeTab === 'overview' && <OverviewTab candidate={candidate} />}
            {activeTab === 'application' && <ApplicationTab candidate={candidate} />}
            {activeTab === 'assessments' && <AssessmentsTab candidate={candidate} />}
            {activeTab === 'notes' && <NotesTab candidateId={id!} existingNotes={candidate.notes} />}
        </div>
    );
}

// ── Overview Tab ─────────────────────────────────────────────

function OverviewTab({ candidate }: { candidate: any }) {
    const scores = candidate.scores || [];
    const avgScore = scores.length > 0
        ? scores.reduce((sum: number, s: any) => sum + (s.scorePercent ?? 0), 0) / scores.length
        : null;

    return (
        <div className="card-grid card-grid-2">
            {/* Info Card */}
            <div className="card">
                <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: '14px' }}>Candidate Details</h3>
                <InfoRow label="Name" value={candidate.name} />
                <InfoRow label="Email" value={candidate.email} />
                <InfoRow label="Phone" value={candidate.phone} />
                <InfoRow label="Current Stage" value={candidate.stage?.name || candidate.stage} badge />
                <InfoRow label="Applied" value={candidate.appliedAt ? new Date(candidate.appliedAt).toLocaleDateString() : null} />
                {candidate.tags && candidate.tags.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>Tags</span>
                        <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
                            {candidate.tags.map((t: string) => (
                                <span key={t} className="badge badge-info">{t}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Score Summary Card */}
            <div className="card">
                <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: '14px' }}>Score Summary</h3>
                {avgScore !== null ? (
                    <>
                        <div style={{
                            textAlign: 'center', padding: 24, background: 'var(--color-bg-tertiary)',
                            borderRadius: 12, marginBottom: 16,
                        }}>
                            <div style={{ fontSize: '48px', fontWeight: 800, color: avgScore >= 70 ? '#10b981' : avgScore >= 40 ? '#f59e0b' : '#ef4444' }}>
                                {avgScore.toFixed(0)}%
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>Average Assessment Score</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {scores.map((s: any, i: number) => (
                                <div key={i} className="flex justify-between items-center" style={{
                                    padding: '8px 12px', background: 'var(--color-bg-tertiary)', borderRadius: 8,
                                }}>
                                    <span style={{ fontSize: '13px' }}>{s.assessmentTitle || `Assessment ${i + 1}`}</span>
                                    <span style={{ fontWeight: 700, color: (s.scorePercent || 0) >= 70 ? '#10b981' : '#f59e0b' }}>
                                        {(s.scorePercent || 0).toFixed(0)}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="empty-state" style={{ padding: 24 }}>
                        <div className="icon">📊</div>
                        <h3>No scores yet</h3>
                        <p>Scores will appear once the candidate completes assessments</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function InfoRow({ label, value, badge }: { label: string; value: any; badge?: boolean }) {
    if (!value) return null;
    return (
        <div className="flex justify-between items-center" style={{
            padding: '8px 0', borderBottom: '1px solid var(--color-border)',
        }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{label}</span>
            {badge ? (
                <span className="badge badge-accent">{value}</span>
            ) : (
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{value}</span>
            )}
        </div>
    );
}

// ── Application Tab — Human-Readable Form Data ──────────────

function ApplicationTab({ candidate }: { candidate: any }) {
    const submissions = candidate.submissions || candidate.formSubmissions || [];
    const uploads = candidate.uploads || [];
    const API_BASE = import.meta.env.VITE_API_URL || '/api';

    if (submissions.length === 0 && uploads.length === 0) {
        return (
            <div className="card">
                <div className="empty-state">
                    <div className="icon">📋</div>
                    <h3>No application data</h3>
                    <p>No form submissions found for this candidate</p>
                </div>
            </div>
        );
    }

    // Separate photo uploads from other files
    const photoUploads = uploads.filter((u: any) => u.mimeType?.startsWith('image/'));
    const docUploads = uploads.filter((u: any) => !u.mimeType?.startsWith('image/'));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Profile Photos / Image Uploads */}
            {photoUploads.length > 0 && (
                <div className="card">
                    <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: 16 }}>
                        📷 Uploaded Photos
                    </h3>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {photoUploads.map((u: any) => (
                            <div key={u.id} style={{ textAlign: 'center' }}>
                                <img
                                    src={`${API_BASE}/uploads/${u.storageKey}`}
                                    alt={u.originalFilename || 'Photo'}
                                    style={{
                                        width: 150, height: 150, objectFit: 'cover',
                                        borderRadius: 12, border: '2px solid var(--color-border)',
                                    }}
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 4 }}>
                                    {u.originalFilename || u.fieldKey || 'Photo'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Document Uploads */}
            {docUploads.length > 0 && (
                <div className="card">
                    <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: 16 }}>
                        📎 Uploaded Files
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {docUploads.map((u: any) => (
                            <a
                                key={u.id}
                                href={`${API_BASE}/uploads/${u.storageKey}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '10px 14px', background: 'var(--color-bg-tertiary)',
                                    borderRadius: 10, border: '1px solid var(--color-border)',
                                    textDecoration: 'none', color: 'var(--color-text-primary)',
                                    transition: 'border-color 0.2s',
                                }}
                            >
                                <FileText size={20} style={{ color: '#6366f1', flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        {u.originalFilename || 'File'}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                        {u.mimeType} · {u.sizeBytes ? `${(u.sizeBytes / 1024).toFixed(0)} KB` : ''}
                                    </div>
                                </div>
                                <span style={{ fontSize: '12px', color: '#6366f1' }}>View ↗</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Form Submissions */}
            {submissions.map((sub: any, i: number) => (
                <div key={sub.id || i} className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h3 style={{ fontWeight: 700, fontSize: '14px' }}>
                            {sub.formName || `Submission ${i + 1}`}
                        </h3>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                            {sub.submittedAt ? new Date(sub.submittedAt).toLocaleString() : ''}
                        </span>
                    </div>
                    <FormDataRenderer data={sub.data || sub} />
                </div>
            ))}
        </div>
    );
}

function FormDataRenderer({ data }: { data: any }) {
    if (!data || typeof data !== 'object') {
        return <p style={{ color: '#6b7280', fontSize: '13px' }}>No data</p>;
    }

    // If data has sections (structured form)
    if (data.sections && Array.isArray(data.sections)) {
        return (
            <>
                {data.sections.map((section: any, si: number) => (
                    <div key={si} style={{ marginBottom: 20 }}>
                        {section.title && (
                            <h4 style={{ fontWeight: 700, fontSize: '14px', color: 'var(--color-accent)', marginBottom: 12 }}>
                                {section.title}
                            </h4>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {Object.entries(section.fields || section.data || {}).map(([key, val]) => (
                                <FieldValueRow key={key} label={key} value={val} />
                            ))}
                        </div>
                    </div>
                ))}
            </>
        );
    }

    // Flat key-value data
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(data).map(([key, val]) => (
                <FieldValueRow key={key} label={key} value={val} />
            ))}
        </div>
    );
}

function FieldValueRow({ label, value }: { label: string; value: any }) {
    const humanLabel = label
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();

    // Table / array data
    if (Array.isArray(value)) {
        if (value.length === 0) return null;
        // Array of objects → render as table
        if (typeof value[0] === 'object' && value[0] !== null) {
            const cols = Object.keys(value[0]);
            return (
                <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                        {humanLabel}
                    </span>
                    <table className="data-table">
                        <thead>
                            <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                        </thead>
                        <tbody>
                            {value.map((row: any, ri: number) => (
                                <tr key={ri}>
                                    {cols.map(c => <td key={c}>{String(row[c] ?? '')}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
        // Array of primitives
        return (
            <div className="flex justify-between items-center" style={{
                padding: '8px 0', borderBottom: '1px solid var(--color-border)',
            }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{humanLabel}</span>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{value.join(', ')}</span>
            </div>
        );
    }

    // Object data → nested
    if (typeof value === 'object' && value !== null) {
        return (
            <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                    {humanLabel}
                </span>
                <div style={{ paddingLeft: 16, borderLeft: '2px solid var(--color-border)' }}>
                    {Object.entries(value).map(([k, v]) => (
                        <FieldValueRow key={k} label={k} value={v} />
                    ))}
                </div>
            </div>
        );
    }

    // Boolean
    if (typeof value === 'boolean') {
        return (
            <div className="flex justify-between items-center" style={{
                padding: '8px 0', borderBottom: '1px solid var(--color-border)',
            }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>{humanLabel}</span>
                <span style={{ fontSize: '13px', fontWeight: 500, color: value ? '#10b981' : '#ef4444' }}>
                    {value ? '✓ Yes' : '✗ No'}
                </span>
            </div>
        );
    }

    // Primitive value
    return (
        <div className="flex justify-between items-center" style={{
            padding: '8px 0', borderBottom: '1px solid var(--color-border)',
        }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{humanLabel}</span>
            <span style={{ fontSize: '13px', fontWeight: 500 }}>{String(value ?? '—')}</span>
        </div>
    );
}

// ── Assessments Tab — Scores with AI Details ─────────────────

function AssessmentsTab({ candidate }: { candidate: any }) {
    const attempts = candidate.attempts || candidate.assessmentAttempts || [];
    const scores = candidate.scores || [];

    if (attempts.length === 0 && scores.length === 0) {
        return (
            <div className="card">
                <div className="empty-state">
                    <div className="icon">📝</div>
                    <h3>No assessment attempts</h3>
                    <p>This candidate hasn't taken any assessments yet</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(attempts.length > 0 ? attempts : scores).map((item: any, i: number) => (
                <ScoreCard key={item.id || i} item={item} />
            ))}
        </div>
    );
}

function ScoreCard({ item }: { item: any }) {
    const queryClient = useQueryClient();
    const [showOverride, setShowOverride] = useState<string | null>(null); // tracks which response is being overridden
    const [overrideScore, setOverrideScore] = useState(0);
    const [overrideReason, setOverrideReason] = useState('');

    const overrideMutation = useMutation({
        mutationFn: (body: any) => api.put(`/assessments/scores/${body.scoreId}/override`, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['candidate'] });
            toast.success('Score overridden');
            setShowOverride(null);
        },
        onError: (err: any) => toast.error(err.message),
    });

    const details = item.details || item.scoreDetails || [];
    const responses = item.responses || [];
    const scorePercent = item.scorePercent ?? item.score ?? 0;

    const isCorrectMCQ = (resp: any) => {
        const selected = resp.answer?.selectedOptionIds || [];
        const correct = resp.correctAnswer?.correctOptionIds || [];
        if (!resp.correctAnswer || correct.length === 0) return null; // unknown — no correct answer defined
        if (selected.length === 0) return false; // unanswered
        return JSON.stringify([...selected].sort()) === JSON.stringify([...correct].sort());
    };

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <h3 style={{ fontWeight: 700, fontSize: '14px' }}>
                    {item.assessmentTitle || item.questionPrompt || `Score ${item.id?.substring(0, 8)}`}
                </h3>
                <div className="flex items-center gap-3">
                    <span style={{ fontSize: '24px', fontWeight: 800, color: scorePercent >= 70 ? '#10b981' : scorePercent >= 40 ? '#f59e0b' : '#ef4444' }}>
                        {typeof scorePercent === 'number' ? scorePercent.toFixed(0) : scorePercent}%
                    </span>
                </div>
            </div>

            {/* AI scoring details */}
            {item.explanation && (
                <div style={{ padding: 12, background: 'var(--color-bg-tertiary)', borderRadius: 8, marginBottom: 12, fontSize: '13px' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: '#6366f1', fontSize: '12px' }}>AI Explanation</div>
                    <p style={{ color: '#4b5563', lineHeight: 1.6 }}>{item.explanation}</p>
                </div>
            )}

            {item.confidence !== undefined && (
                <div className="flex gap-3 mb-3">
                    <span className="badge badge-accent">
                        <Star size={12} /> Confidence: {(item.confidence * 100).toFixed(0)}%
                    </span>
                    {item.flagged && (
                        <span className="badge badge-error">
                            <AlertTriangle size={12} /> Flagged for review
                        </span>
                    )}
                </div>
            )}

            {/* Per-question breakdown from responses */}
            {responses.length > 0 && (
                <div>
                    <h4 style={{ fontWeight: 600, fontSize: '13px', color: '#4b5563', marginBottom: 8 }}>Responses</h4>
                    {responses.map((resp: any, i: number) => {
                        const subScore = resp.subjectiveScore;
                        const hasSubjective = !!subScore;
                        const isSpeaking = resp.questionType === 'speaking';
                        return (
                            <div key={resp.id || i} style={{
                                padding: '10px 12px', marginBottom: 6,
                                background: 'var(--color-bg-tertiary)', borderRadius: 6, fontSize: '13px',
                            }}>
                                <div className="flex justify-between items-center">
                                    <span style={{ flex: 1, fontWeight: 500 }}>
                                        Q{i + 1}. {resp.questionText || resp.questionType || '—'}
                                    </span>
                                    <span style={{ 
                                        fontWeight: 700, 
                                        marginLeft: 12,
                                        color: hasSubjective 
                                            ? ((() => { const pct = subScore.maxScore > 0 ? (subScore.score / subScore.maxScore) * 100 : 0; return pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444'; })())
                                            : resp.questionType === 'mcq' 
                                                ? (isCorrectMCQ(resp) === true ? '#10b981' : isCorrectMCQ(resp) === false ? '#ef4444' : '#6b7280') 
                                                : 'var(--color-text-secondary)'
                                    }}>
                                        {hasSubjective 
                                            ? `${subScore.maxScore > 0 ? ((subScore.score / subScore.maxScore) * 100).toFixed(0) : subScore.score ?? '—'}%` 
                                            : resp.questionType === 'mcq' 
                                                ? (isCorrectMCQ(resp) === true ? '✓ Correct' : isCorrectMCQ(resp) === false ? '✗ Incorrect' : '—') 
                                                : isSpeaking ? 'Video' : '—'}
                                    </span>
                                    {hasSubjective && (
                                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }}
                                            onClick={() => { setShowOverride(subScore.id); setOverrideScore(subScore.score || 0); setOverrideReason(''); }}>
                                            <Edit2 size={12} /> Override
                                        </button>
                                    )}
                                </div>
                                {resp.questionType === 'mcq' && resp.correctAnswer && (() => {
                                    const selected = resp.answer?.selectedOptionIds || [];
                                    const correct = resp.correctAnswer.correctOptionIds || [];
                                    const options = resp.correctAnswer.options || [];
                                    return (
                                        <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {options.map((opt: any) => {
                                                const isSelected = selected.includes(opt.id);
                                                const isCorrectOpt = correct.includes(opt.id);
                                                return (
                                                    <div key={opt.id} style={{
                                                        padding: '6px 10px', borderRadius: 8, fontSize: '12px',
                                                        background: isCorrectOpt ? 'rgba(16, 185, 129, 0.06)' : isSelected ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
                                                        border: `1px solid ${isCorrectOpt ? '#10b981' : isSelected ? '#ef4444' : 'var(--color-border)'}`,
                                                        display: 'flex', alignItems: 'center', gap: 6,
                                                        color: isCorrectOpt ? '#10b981' : isSelected ? '#ef4444' : 'var(--color-text-primary)'
                                                    }}>
                                                        {isSelected && isCorrectOpt && <span style={{ fontWeight: 'bold' }}>✓</span>}
                                                        {isSelected && !isCorrectOpt && <span style={{ fontWeight: 'bold' }}>✗</span>}
                                                        {!isSelected && isCorrectOpt && <span>✓</span>}
                                                        <span>{opt.text}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                                {isSpeaking && resp.answer?.videoKey && (
                                    <div style={{ marginTop: 8 }}>
                                        <video
                                            src={`${API_BASE}/uploads/${resp.answer.videoKey}`}
                                            controls
                                            style={{
                                                width: '100%',
                                                maxWidth: 400,
                                                borderRadius: 8,
                                                background: '#000',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </div>
                                )}
                                {/* Inline override form */}
                                {showOverride === subScore?.id && (
                                    <div style={{ marginTop: 10, padding: 12, background: 'rgba(239, 68, 68, 0.05)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                                        <div className="flex gap-3 mb-2">
                                            <div className="form-group" style={{ flex: 0, marginBottom: 0 }}>
                                                <label className="form-label" style={{ fontSize: '11px' }}>New Score</label>
                                                <input type="number" className="form-input" value={overrideScore}
                                                    onChange={(e) => setOverrideScore(Number(e.target.value))}
                                                    min={0} max={100} style={{ width: 80 }} />
                                            </div>
                                            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                                                <label className="form-label" style={{ fontSize: '11px' }}>Reason</label>
                                                <input className="form-input" value={overrideReason}
                                                    onChange={(e) => setOverrideReason(e.target.value)}
                                                    placeholder="Why are you overriding?" />
                                            </div>
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <button className="btn btn-ghost btn-sm" onClick={() => setShowOverride(null)}>Cancel</button>
                                            <button className="btn btn-primary btn-sm" onClick={() => {
                                                overrideMutation.mutate({ scoreId: subScore.id, score: overrideScore, reason: overrideReason });
                                            }} disabled={overrideMutation.isPending}>
                                                <Save size={14} /> {overrideMutation.isPending ? 'Saving...' : 'Submit'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Legacy per-question breakdown (if no responses) */}
            {responses.length === 0 && details.length > 0 && (
                <div>
                    <h4 style={{ fontWeight: 600, fontSize: '13px', color: '#4b5563', marginBottom: 8 }}>Per-Question Breakdown</h4>
                    {details.map((d: any, i: number) => (
                        <div key={i} className="flex justify-between items-center"
                            style={{
                                padding: '8px 12px', marginBottom: 4,
                                background: 'var(--color-bg-tertiary)', borderRadius: 6, fontSize: '13px',
                            }}>
                            <span style={{ flex: 1 }}>{d.questionPrompt || d.question || `Q${i + 1}`}</span>
                            <span style={{ fontWeight: 700, marginLeft: 12 }}>{d.score ?? '—'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Notes Tab ────────────────────────────────────────────────

function NotesTab({ candidateId, existingNotes }: { candidateId: string; existingNotes?: any[] }) {
    const [note, setNote] = useState('');
    const queryClient = useQueryClient();
    const [notes, setNotes] = useState<any[]>(existingNotes || []);

    const saveMutation = useMutation({
        mutationFn: (updatedNotes: any[]) => api.put(`/candidates/${candidateId}/notes`, { notes: updatedNotes }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['candidate', candidateId] });
        },
        onError: (err: any) => toast.error(err.message || 'Failed to save note'),
    });

    const addNote = () => {
        if (!note.trim()) return;
        const newNotes = [{ text: note, date: new Date().toISOString(), author: 'You' }, ...notes];
        setNotes(newNotes);
        setNote('');
        saveMutation.mutate(newNotes);
        toast.success('Note added');
    };

    const deleteNote = (index: number) => {
        const newNotes = notes.filter((_, i) => i !== index);
        setNotes(newNotes);
        saveMutation.mutate(newNotes);
        toast.success('Note removed');
    };

    return (
        <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: '14px' }}>Internal Notes</h3>
            <div className="form-group">
                <textarea className="form-textarea" value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note about this candidate..."
                    rows={3} />
            </div>
            <button className="btn btn-primary mb-6" onClick={addNote} disabled={saveMutation.isPending}>
                <MessageSquare size={16} /> {saveMutation.isPending ? 'Saving...' : 'Add Note'}
            </button>

            {notes.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>
                    <div className="icon">📝</div>
                    <h3>No notes</h3>
                    <p>Add internal notes about this candidate for your team</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {notes.map((n, i) => (
                        <div key={i} style={{
                            padding: '12px 16px', background: 'var(--color-bg-tertiary)',
                            borderRadius: 8, fontSize: '13px', position: 'relative',
                        }}>
                            <p style={{ marginBottom: 4, lineHeight: 1.5 }}>{n.text}</p>
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                {n.author} · {new Date(n.date).toLocaleString()}
                            </span>
                            <button className="btn btn-ghost btn-sm" style={{
                                position: 'absolute', top: 8, right: 8, padding: 4, opacity: 0.5,
                            }} onClick={() => deleteNote(i)}>
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
